import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { COMPANY, MAIN_NAV, waLink, WA_MESSAGES } from "@/data/company";
import { SERVICES, SPECIAL_SOLUTIONS } from "@/data/services";
import { CTAButton } from "@/components/common/Bits";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openDrop, setOpenDrop] = useState(null);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setOpenDrop(null);
    }, [location.pathname]);

    // Every page except the homepage opens on a dark hero (PageHero, and the
    // brand-blue headers on articles and projects). Before the header picks up
    // its white background on scroll, its text has to be light there or it is
    // dark-on-dark and effectively invisible.
    const overDarkHero = !scrolled && location.pathname !== "/";

    const dropItems = (key) =>
        key === "services"
            ? SERVICES.map((s) => ({ label: s.name, to: `/services/${s.slug}` }))
            : SPECIAL_SOLUTIONS.map((s) => ({
                  label: s.name,
                  to: `/special-solutions/${s.slug}`,
              }));

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "border-b border-brand-blue/10 bg-white/85 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
            data-testid="navbar"
        >
            <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-6">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
                    <img src={COMPANY.logo} alt="ProCare.SG logo" className="h-11 w-11 object-contain" />
                    <span className="font-display text-xl font-semibold tracking-tight">
                        <span className={overDarkHero ? "text-white" : "text-brand-blue"}>Pro</span>
                        <span className="text-brand-red">Care</span>
                        <span className={overDarkHero ? "text-white" : "text-brand-blue"}>.SG</span>
                    </span>
                </Link>

                {/* Desktop nav */}
                <ul className="hidden items-center gap-1 min-[1150px]:flex">
                    {MAIN_NAV.map((item) => (
                        <li
                            key={item.label}
                            className="relative"
                            onMouseEnter={() => item.dropdown && setOpenDrop(item.dropdown)}
                            onMouseLeave={() => item.dropdown && setOpenDrop(null)}
                        >
                            <Link
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                                    location.pathname === item.to
                                        ? "text-brand-red"
                                        : overDarkHero
                                          ? "text-white/85 hover:text-white"
                                          : "text-brand-ink hover:text-brand-blue"
                                }`}
                            >
                                {item.label}
                                {item.dropdown && <ChevronDown className="h-3.5 w-3.5" />}
                            </Link>
                            {item.dropdown && openDrop === item.dropdown && (
                                <div className="absolute left-0 top-full w-64 pt-2">
                                    <div className="overflow-hidden rounded-2xl border border-brand-blue/10 bg-white p-2 shadow-brand-lg">
                                        {dropItems(item.dropdown).map((d) => (
                                            <Link
                                                key={d.to}
                                                to={d.to}
                                                data-testid={`dropdown-${d.to.split("/").pop()}`}
                                                className="block rounded-xl px-4 py-2.5 text-sm text-brand-ink/80 transition-colors hover:bg-brand-surface hover:text-brand-red"
                                            >
                                                {d.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>

                <div className="hidden min-[1150px]:block">
                    <CTAButton
                        href={waLink(WA_MESSAGES.quote)}
                        whatsapp
                        testid="nav-quote-cta"
                    >
                        Get a Free Quote
                    </CTAButton>
                </div>

                {/* Mobile toggle */}
                <button
                    className={`rounded-full p-2 min-[1150px]:hidden ${overDarkHero && !mobileOpen ? "text-white" : "text-brand-blue"}`}
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                >
                    {mobileOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
                </button>
            </nav>

            {/* Mobile menu */}
            {mobileOpen && (
                <div className="max-h-[80vh] overflow-y-auto border-t border-brand-blue/10 bg-white px-5 py-4 min-[1150px]:hidden" data-testid="mobile-menu">
                    <ul className="space-y-1">
                        {MAIN_NAV.map((item) => (
                            <li key={item.label}>
                                {item.dropdown ? (
                                    <div>
                                        <button
                                            className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left font-medium text-brand-blue"
                                            onClick={() =>
                                                setOpenDrop(openDrop === item.dropdown ? null : item.dropdown)
                                            }
                                        >
                                            {item.label}
                                            <ChevronDown
                                                className={`h-4 w-4 transition-transform ${
                                                    openDrop === item.dropdown ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        {openDrop === item.dropdown && (
                                            <div className="ml-3 border-l-2 border-brand-red/30 pl-3">
                                                {dropItems(item.dropdown).map((d) => (
                                                    <Link
                                                        key={d.to}
                                                        to={d.to}
                                                        className="block rounded-lg px-3 py-2 text-sm text-brand-ink/70"
                                                    >
                                                        {d.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Link
                                        to={item.to}
                                        className="block rounded-xl px-3 py-3 font-medium text-brand-ink"
                                    >
                                        {item.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-4">
                        <CTAButton
                            href={waLink(WA_MESSAGES.quote)}
                            whatsapp
                            className="w-full"
                            testid="mobile-quote-cta"
                        >
                            Get a Free Quote
                        </CTAButton>
                    </div>
                </div>
            )}
        </header>
    );
}
